import { Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/shared/lib';

/** Пункт списка в меню свёрнутого корневого элемента сайдбара */
export type SidebarFlyoutItem = {
  path: string;
  icon: ReactNode;
  label: ReactNode;
};

type Props = {
  /** Заголовок меню — название раздела/пункта */
  title: ReactNode;
  /** Пункты подменю; если не переданы — показывается только заголовок */
  items?: SidebarFlyoutItem[];
  children: ReactNode;
};

const CLOSE_DELAY_MS = 350;

/**
 * Всплывающее меню при наведении на свёрнутую иконку корневого пункта сайдбара:
 * сверху — заголовок (название раздела), ниже, если это группа — список её пунктов.
 * Открывается по hover/focus и закрывается с небольшой задержкой, чтобы курсор успевал
 * переместиться с иконки на содержимое меню (иначе оно закрывалось бы раньше клика).
 */
export const SidebarFlyoutMenu: FC<Props> = ({ title, items, children }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleOpen = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const handleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} trigger={'hover'}>
      <PopoverTrigger asChild>
        <div onFocus={handleOpen} onBlur={handleClose}>
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent
        side={'right'}
        align={'start'}
        sideOffset={8}
        className={'p-0 w-56'}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
      >
        <div className={'px-3 py-2 border-b border-border text-sm font-medium text-foreground'}>{title}</div>
        {!!items?.length && (
          <div className={'flex flex-col p-1'}>
            {items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground',
                    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                    '[&>svg]:size-4 [&>svg]:shrink-0',
                    isActive && 'bg-sidebar-accent text-sidebar-accent-foreground',
                  )
                }
              >
                {item.icon}
                <span className={'truncate'}>{item.label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
